import { Capacitor } from "@capacitor/core";
import { supported as mapboxSupported, notSupportedReason } from "@mapbox/mapbox-gl-supported";
import Bowser from "bowser";
import {
    BfBuild,
    BfClientConfig,
    BfClientType,
    BfFirebase,
    BfMapId,
    BfMapLayerGroupId,
} from "./api/models";

export const DEBUG_MODE =
    import.meta.env.NODE_ENV === "development" || import.meta.env.VITE_DEBUG_MODE === "true";
export const DEBUG_GPU_MEMORY = import.meta.env.VITE_DEBUG_GPU_MEMORY === "true";
export const PERMIT_CUSTOM_CLIENT_API = import.meta.env.VITE_PERMIT_CUSTOM_CLIENT_API === "true";
export const DEBUG_TILES = import.meta.env.VITE_DEBUG_TILES === "true";
export const V2_STATE_STORAGE_KEY = "bfstate";
export const V2_DEVICE_ID_KEY = "deviceid";
export const V3_STATE_STORAGE_KEY = "bf3client";
export const V4_PREFS_STORAGE_KEY = "bf4prefs";
export const EXPECTED_EULA_VERSION = "4";
export const TIME_BETWEEN_SAVE_SECS = 10;
export const OLD_CACHE_NAME = "cache-v1";
export const OLD_PRECACHE_CACHE_NAME = "workbox-precache-v2";
export const PRECACHE_CACHE_NAME = "ds-precache-v1";
export const TILEY_CACHE_NAME = "tiley";
export const IMAGES_CACHE_NAME = "images";
export const API_CACHE_NAME = "api";
export const MAPBOX_CACHE_NAME = "mapbox-tiles";

export enum CurrentDeviceBreakpoint {
    Xxxs = 300,
    Xxs = 375,
    Xs = 576,
    S = 768,
    Md = 992,
    L = 1200,
    Xl = 1400,
    Xxl = Number.MAX_SAFE_INTEGER,
}

export class LocalStorageClientSettings {
    // Has the API been successfully contacted once (will be saved to storage)
    _clientContactedAPIEver: boolean | null = null;

    // Set after connecting to the API, or loaded from storage
    _clientToken: string | null = null;

    // The session's companion token for tiley; absent while the session is still a JWT
    _clientTileToken?: string | null = null;

    // Device unique id
    _clientDeviceId: string = "";

    // Store if the user has accepted the EULA on this device
    _clientMostRecentEULAAccepted: string | null = null;

    // News item ids the user has seen; null until first initialised
    _clientNewsSeenIds: string[] | null = null;

    // Config retrieved from API
    _configFromApi: BfClientConfig | null = null;
}

export class InstanceRendering {
    showMap: boolean = true;
    showNotifications: boolean = true;
    showOrganisation: boolean = true;
    showPlans: boolean = true;
    showFeedback: boolean = true;
    showLogin: boolean = true;
    showAccount: boolean = true;
    showNotificationsInbox: boolean = true;
    showNotificationsSettings: boolean = true;
    showNotificationsCreate: boolean = true;
    showMore: boolean = true;
    showMoreDiagnostics: boolean = false;
    showMoreFaq: boolean = true;
    showMoreSupportedDevices: boolean = true;
    showMoreWelcome: boolean = true;
    showMoreWorkWithUs: boolean = true;
    showMoreAbout: boolean = true;
    showMoreSystemStatus: boolean = true;
    showMorePrivacyPolicy: boolean = true;
    showMoreTermsOfService: boolean = true;
    showMoreSocialLinks: boolean = true;
    showMedia: boolean = true;
    showWhatsNew: boolean = true;
}

export const firebase = (): BfFirebase | null => {
    // These values may be built into the bundle. If they are, the service-worker doesn't
    // need to contact the API. Useful for clients which haven't been updated, but their sw remain active.
    return import.meta.env.VITE_CLIENT_FIREBASE_API_KEY &&
        import.meta.env.VITE_CLIENT_FIREBASE_PROJECT_ID &&
        import.meta.env.VITE_CLIENT_FIREBASE_SENDER_ID &&
        import.meta.env.VITE_CLIENT_FIREBASE_APP_ID
        ? {
              apiKey: import.meta.env.VITE_CLIENT_FIREBASE_API_KEY,
              projectId: import.meta.env.VITE_CLIENT_FIREBASE_PROJECT_ID,
              messagingSenderId: import.meta.env.VITE_CLIENT_FIREBASE_SENDER_ID,
              appId: import.meta.env.VITE_CLIENT_FIREBASE_APP_ID,
          }
        : null;
};

export const apiUrl = (): string => {
    return import.meta.env.VITE_CLIENT_API;
};

export const apiSpecialInstance = (): BfBuild => {
    const url = apiUrl();
    if (url.includes("api-qa")) return BfBuild.qa;
    if (url.includes("localhost")) return BfBuild.dev;
    if (url.includes("api-dev")) return BfBuild.dev;
    return BfBuild.prod;
};

export const serviceWorkerUpdateInterval = () => {
    return import.meta.env.VITE_SW_CHECK_FREQ_MINS
        ? parseInt(import.meta.env.VITE_SW_CHECK_FREQ_MINS)
        : 60;
};

export const websocketUrl = (): string => {
    return apiUrl().replace("https://", "wss://").replace("http://", "ws://");
};

export const isiOSApp = () => {
    return Capacitor.getPlatform() === "ios";
};

export const isAndroidApp = () => {
    return Capacitor.getPlatform() === "android";
};

export const isPwaTwa = () => {
    if (window.matchMedia("(display-mode: standalone)").matches) {
        return true;
    }
    return false;
};

export const isMobileApp = () => {
    return isiOSApp() || isAndroidApp() || isPwaTwa();
};

export const isCapacitor = () => {
    return Capacitor.isNativePlatform();
};

export const getClientSimple = (): BfClientType => {
    if (isiOSApp()) {
        return BfClientType.ios;
    }

    if (isAndroidApp()) {
        return BfClientType.android;
    }

    if (isPwaTwa()) {
        return BfClientType.pwatwa;
    }

    return BfClientType.web;
};

export const getCient = () => {
    if (isiOSApp()) {
        // Original (non SFSafariViewController login)
        // returned BfClientType.ios;

        return BfClientType.iossigninwithapple;
    }

    if (isAndroidApp()) {
        return BfClientType.android;
    }

    if (isPwaTwa()) {
        return BfClientType.pwatwa;
    }

    return BfClientType.web;
};

export const supported = () => {
    if (!localStorageSupported()) {
        return false;
    }

    if (!mapboxSupported()) {
        console.warn!("Mapbox GL is not supported", notSupportedReason());
        return false;
    }

    if (!webGLSupported()) {
        return false;
    }

    return true;
};

export const isWebKit = () => {
    if (typeof window !== "undefined") {
        const browser = Bowser.getParser(window.navigator.userAgent);
        return browser.getEngine()?.name === "WebKit";
    } else {
        return false;
    }
};

export const isGecko = () => {
    return navigator.userAgent.includes("Gecko") && !navigator.userAgent.includes("like Gecko");
};

export const isSpecialInstance = (): BfBuild => {
    // Handle Android capacitor
    if (window.location.href.includes("localhost") && window.location.protocol.includes("https")) {
        return BfBuild.prod;
    }

    if (
        window.location.href.includes("localhost") &&
        !window.location.href.includes("bushfireio://")
    )
        return BfBuild.dev;

    if (window.location.href.includes("qa.")) return BfBuild.qa;

    if (window.location.href.includes("dev.")) return BfBuild.dev;

    return BfBuild.prod;
};

/// Determine (via CSS query) if the user is currently using a dark theme
export const isDarkColourSchemeUsed = (): boolean => {
    const userPrefersDark =
        window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    return userPrefersDark;
};

/// Returns the Twitter Bootstrap breakpoint in use
export const breakpoint = (): number => {
    function enumKeys<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
        return Object.keys(obj).filter((k) => Number.isNaN(+k)) as K[];
    }

    const currentWindowWidth = window.innerWidth;
    const size = CurrentDeviceBreakpoint.Xxxs;

    // Handle else case
    if (currentWindowWidth >= 1400) {
        return CurrentDeviceBreakpoint.Xxl;
    }
    // Iterate through widths Map<(name: maximum supported size, in px)>
    let previous = null;
    for (const width of enumKeys(CurrentDeviceBreakpoint)) {
        // Is current bigger than this max, if so try the next
        if (currentWindowWidth <= CurrentDeviceBreakpoint[width]) {
            return previous ? previous : CurrentDeviceBreakpoint[width];
        }
        previous = CurrentDeviceBreakpoint[width];
    }

    return size;
};

/// Check we have access to local storage, required to run the app
export const localStorageSupported = (): boolean => {
    try {
        const supported = "localStorage" in window && window["localStorage"] !== null;
        if (!supported) {
            console.warn("Local storage is not supported");
        }
        return supported;
    } catch (e) {
        console.warn("Local storage is not supported, caught error", e);
        return false;
    }
};

/// Check if webgl is supported
export const webGLSupported = (): boolean => {
    try {
        if (DEBUG_MODE) {
            // If this happens too often, Android will begin crashing the app
            console.warn!("Client checking webgl support getContext");
        }
        const canvas = document.createElement("canvas");
        const supported = !!(window.WebGLRenderingContext && canvas.getContext("webgl2"));

        if (!supported) {
            console.error("WebGL is not supported");
        }
        return supported;
    } catch (e) {
        console.error("WebGL is not supported, caught error", e);
        return false;
    }
};

export const webWorkerSupported = (): boolean => {
    const supported = !!window.Worker;

    if (!supported) {
        console.warn("Web Workers are not supported");
    }

    return supported;
};

export const offscreenCanvasSupported = (): boolean => {
    // check for window.offscreencanvas and HTMLCanvasElement.prototype.transferControlToOffscreen
    // Using similar logic to Mapbox GL JS check

    const supported =
        // Ensure offscreencanvas is present
        !!window.OffscreenCanvas &&
        // Ensure it can be initialised
        !!new window.OffscreenCanvas(1, 1).getContext("2d") &&
        // Ensure there's a transfer method available
        !!HTMLCanvasElement.prototype.transferControlToOffscreen &&
        // Ensure an image can be created, used by Mapbox
        !!(typeof window.createImageBitmap === "function") &&
        // Ensure not native Gecko
        !isGecko() &&
        // at v140, and there seems to be an issue with
        // requestAnimationFrame only providing 10 frames to render
        // and then no more! So disable for now.
        !isAndroidApp();

    if (DEBUG_MODE) {
        console.log("Client checking offscreen canvas getContext", supported);
    }

    if (!supported) {
        console.warn("Client OffscreenCanvas is not supported");
    }

    return supported;
};

export const enableLogin = () => {
    if (import.meta.env.VITE_DISABLE_LOGIN === "true") {
        return false;
    }
    return true;
};

export const enableEula = () => {
    if (import.meta.env.VITE_DISABLE_EULA === "true") {
        return false;
    }
    return true;
};

export const enableSocials = () => {
    if (import.meta.env.VITE_DISABLE_SOCIAL === "true") {
        return false;
    }
    return true;
};

export const enableNotifications = () => {
    if (import.meta.env.VITE_DISABLE_NOTIFICATION === "true") {
        return false;
    }
    return true;
};

export const enableFeedback = () => {
    if (import.meta.env.VITE_DISABLE_FEEDBACK === "true") {
        return false;
    }
    return true;
};

export const enablePayments = () => {
    if (import.meta.env.VITE_DISABLE_PAYMENT === "true") {
        return false;
    }
    return true;
};

export const enableManagement = () => {
    if (import.meta.env.VITE_DISABLE_MANAGEMENT === "true") {
        return false;
    }
    return true;
};

export const hideSidebarByDefault = () => {
    if (import.meta.env.VITE_HIDE_SIDEBAR_BY_DEFAULT === "true") {
        return false;
    }
    return true;
};

export const gitHash = (): string => {
    return import.meta.env.GIT_HASH;
};

export const gitBranch = (): string => {
    return import.meta.env.GIT_BRANCH;
};

export const gitDate = (): string => {
    return import.meta.env.GIT_DATE;
};

export const clientVersion = (): string => {
    return import.meta.env.PACKAGE_VERSION || "3.1.0";
};
export const clientName = (): string => {
    return import.meta.env.PACKAGE_NAME || "ux";
};

export const getStateFromLocalStorage = <T>(key: string): T | undefined => {
    try {
        if (typeof window !== "undefined") {
            const serializedState = window.localStorage.getItem(key);
            if (serializedState === null) {
                // No state saved
                return undefined;
            }

            const payload: T = JSON.parse(serializedState);

            return payload;
        }
    } catch (err) {
        console.warn("Failed to load state from local storage", err);
    }
};

export const saveStateToLocalStorage = <T>(key: string, state: T): void => {
    try {
        const serializedState = JSON.stringify(state, (k, v) => {
            if (k === "subscribers" || k === "localStorageKey") {
                return undefined;
            }
            return v;
        });

        window.localStorage.setItem(key, serializedState);
    } catch (e) {
        console.warn("Failed to save state to local storage", e);
    }
};

export const deleteStateFromLocalStorage = (key: string) => {
    return window.localStorage.removeItem(key);
};

export const isOnline = () => {
    return navigator.onLine;
};

export const getServiceWorker = () => {
    if (!("serviceWorker" in navigator)) {
        return Promise.reject("Service workers are not supported by this browser");
    }
    return navigator.serviceWorker.ready;
};

const unloadServiceWorker = async () => {
    if (!("serviceWorker" in navigator)) {
        return Promise.reject("Service workers are not supported by this browser");
    }

    const registration = await getServiceWorker();
    return await registration.unregister();
};

export const destroyEverything = () => {
    // Remove old v1 cache
    caches.delete(OLD_CACHE_NAME);

    // Assume this is broken for some clients
    caches.delete(OLD_PRECACHE_CACHE_NAME);

    // Remove our precache
    caches.delete("ds-precache-v1");

    // Remove all other caches
    caches.delete(TILEY_CACHE_NAME);
    caches.delete(IMAGES_CACHE_NAME);
    caches.delete(API_CACHE_NAME);
    caches.delete(MAPBOX_CACHE_NAME);

    return unloadServiceWorker();
};

export const MAP_PREFERENCE_KEYS: Record<BfMapId, string> = {
    [BfMapId.main]: `${V4_PREFS_STORAGE_KEY}-main`,
    [BfMapId.notification]: `${V4_PREFS_STORAGE_KEY}-notification`,
    [BfMapId.manage]: `${V4_PREFS_STORAGE_KEY}-manage`,
    [BfMapId.managePreview]: `${V4_PREFS_STORAGE_KEY}-managePreview`,
    [BfMapId.customLayerEditor]: `${V4_PREFS_STORAGE_KEY}-customLayerEditor`,
};

export const COMBINED_EVENT_GROUP_IDS: BfMapLayerGroupId[] = [
    BfMapLayerGroupId.warnings,
    BfMapLayerGroupId.incidents,
    BfMapLayerGroupId.weatherWarnings,
    BfMapLayerGroupId.burnAreas,
    BfMapLayerGroupId.plannedBurns,
    BfMapLayerGroupId.ignitions,
];
